/**
 * Page de détails d'un dossier ANEF - Design mobile-first
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Chip,
  Button,
  LinearProgress,
  Paper,
  IconButton,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Fab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Warning,
  Error,
  Info,
  Schedule,
  VolumeUp,
  VolumeOff,
  Description,
  Sync,
  HourglassEmpty,
  ExpandMore,
  Notifications,
} from '@mui/icons-material';
import { clientDossiersApi, Dossier } from '../../services/clientApi';

export default function ClientDossierDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [dossier, setDossier] = useState<Dossier | null>(null);
  const [loading, setLoading] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(() => {
    return localStorage.getItem('voice_enabled') === 'true';
  });

  useEffect(() => {
    if (id) {
      fetchDossier();
    }
  }, [id]);

  const fetchDossier = async () => {
    try {
      setLoading(true);
      const response = await clientDossiersApi.getById(parseInt(id!));
      setDossier(response.data.dossier);

      // Marquer automatiquement tous les événements comme lus
      try {
        await clientDossiersApi.markEventsAsRead(parseInt(id!));
        console.log('✅ Événements marqués comme lus');
      } catch (error: any) {
        // Ignorer les erreurs 404 (dossier sans événements ou n'appartenant pas au client)
        if (error?.response?.status !== 404) {
          console.error('Erreur marquage événements:', error);
        }
      }
    } catch (error) {
      console.error('Erreur chargement dossier:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await clientDossiersApi.syncAll();
      await fetchDossier();
    } catch (error) {
      console.error('Erreur sync:', error);
    } finally {
      setSyncing(false);
    }
  };

  const handleSpeakText = (text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();

      if (speaking) {
        setSpeaking(false);
        return;
      }

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'fr-FR';
      utterance.rate = 0.9;
      utterance.pitch = 1;

      utterance.onstart = () => setSpeaking(true);
      utterance.onend = () => setSpeaking(false);
      utterance.onerror = () => setSpeaking(false);

      window.speechSynthesis.speak(utterance);
    } else {
      alert('La synthèse vocale n\'est pas disponible sur votre appareil');
    }
  };

  const getStepIcon = (type: string) => {
    switch (type) {
      case 'DEPOT':
        return <CheckCircle />;
      case 'COMPLEMENT':
        return <Warning />;
      case 'INSTRUCTION':
        return <HourglassEmpty />;
      case 'TITRE':
        return <CheckCircle />;
      default:
        return <Info />;
    }
  };

  // Obtenir un message détaillé basé sur le statut ANEF
  const getDetailedStatusMessage = (statut: string | undefined): string[] => {
    if (!statut) return ['Votre dossier est en cours d\'examen par la préfecture'];

    const statutUpper = statut.toUpperCase();

    switch (statutUpper) {
      case 'ATTENTE_BIOMETRIE':
        return [
          'Votre dossier est en attente de prise d\'empreintes biométriques',
          'Vous serez très prochainement convoqué par la préfecture pour effectuer vos empreintes',
          'Si vous avez déjà été convoqué, la préfecture est en train d\'enregistrer vos empreintes dans le système'
        ];

      case 'SOUMIS_A_VALIDATION':
        return [
          'Votre dossier est en cours d\'examen par la préfecture',
          'L\'administration étudie actuellement votre demande en vue de donner une décision',
          'Cette étape peut prendre plusieurs semaines selon la complexité de votre dossier'
        ];

      case 'COMPLEMENT_RECU':
        return [
          'Vous avez récemment répondu à une demande de complément',
          'Vos documents n\'ont pas encore été validés par la préfecture',
          'L\'administration va examiner les pièces que vous avez fournies'
        ];

      case 'EN_INSTRUCTION':
      case 'INSTRUCTION_EN_COURS':
        return [
          'Votre dossier est en cours d\'instruction',
          'La préfecture examine actuellement l\'ensemble de votre demande',
          'Vous serez informé de toute évolution ou demande complémentaire'
        ];

      case 'ATTENTE_CONVOCATION':
        return [
          'Votre dossier est en attente de convocation',
          'La préfecture va vous convoquer prochainement pour la suite de votre demande',
          'Surveillez régulièrement vos notifications'
        ];

      case 'DECISION_FAVORABLE':
        return [
          'Décision favorable ! Votre demande a été acceptée',
          'Vous allez recevoir votre titre de séjour',
          'Consultez les notifications pour connaître les modalités de retrait'
        ];

      case 'TITRE_DISPONIBLE':
        return [
          'Votre titre de séjour est disponible',
          'Vous pouvez le retirer auprès de la préfecture',
          'Pensez à vous munir des documents nécessaires pour le retrait'
        ];

      default:
        // Cas générique : essayer de détecter des mots-clés
        if (statutUpper.includes('ATTENTE') && statutUpper.includes('BIOM')) {
          return getDetailedStatusMessage('ATTENTE_BIOMETRIE');
        }
        if (statutUpper.includes('COMPLEMENT')) {
          return getDetailedStatusMessage('COMPLEMENT_RECU');
        }
        if (statutUpper.includes('INSTRUCTION')) {
          return getDetailedStatusMessage('EN_INSTRUCTION');
        }
        if (statutUpper.includes('VALIDATION')) {
          return getDetailedStatusMessage('SOUMIS_A_VALIDATION');
        }

        return [
          `Statut actuel : ${statut.replace(/_/g, ' ')}`,
          'Votre dossier est en cours de traitement par la préfecture'
        ];
    }
  };

  // Construire la timeline du dossier
  const buildTimeline = () => {
    const timeline: any[] = [];

    if (!dossier) return timeline;

    // 1. Dépôt de la demande
    if (dossier.anef_date_depot) {
      timeline.push({
        type: 'DEPOT',
        label: 'Dépôt de la demande',
        date: dossier.anef_date_depot,
        completed: true,
        details: `Demande de ${dossier.type_demande} déposée`,
      });
    }

    // 2. Demandes de complément (seulement celles NON répondues)
    if (dossier.anef_demandes_complement && dossier.anef_demandes_complement.length > 0) {
      const complementsNonRepondus = dossier.anef_demandes_complement.filter((c: any) => !c.date_reponse);

      if (complementsNonRepondus.length > 0) {
        const dernierComplement = complementsNonRepondus[0];
        const observations: string[] = [];

        if (dernierComplement.justificatifs) {
          dernierComplement.justificatifs.forEach((j: any) => {
            if (j.justificatif?.historique_echanges?.[0]?.observation) {
              observations.push(j.justificatif.historique_echanges[0].observation);
            }
          });
        }

        timeline.push({
          type: 'COMPLEMENT',
          label: 'Documents complémentaires demandés',
          date: dernierComplement._created,
          completed: false,
          current: true,
          details: observations.length > 0 ? observations : ['La préfecture a demandé des documents complémentaires'],
          canSpeak: observations.length > 0,
        });
      }
    }

    // 3. Instruction en cours
    const statutLower = dossier.anef_statut_actuel?.toLowerCase() || '';
    if (statutLower.includes('instruction') || statutLower.includes('attente') ||
        statutLower.includes('soumis') || statutLower.includes('complement_recu')) {
      const complementRepondu = dossier.anef_demandes_complement?.some((c: any) => c.date_reponse);

      // Déterminer le label basé sur le statut
      let label = 'Instruction en cours';
      if (complementRepondu) {
        label = 'Complément reçu - Instruction en cours';
      } else if (statutLower.includes('attente_biometrie')) {
        label = 'En attente d\'empreintes biométriques';
      } else if (statutLower.includes('soumis_a_validation')) {
        label = 'Dossier soumis à validation';
      } else if (statutLower.includes('complement_recu')) {
        label = 'Complément reçu - En attente de validation';
      }

      // Obtenir les détails basés sur le statut actuel
      const detailsFromStatus = getDetailedStatusMessage(dossier.anef_statut_actuel);

      timeline.push({
        type: 'INSTRUCTION',
        label: label,
        date: dossier.anef_derniere_mise_a_jour,
        completed: false,
        current: !dossier.anef_has_complement || complementRepondu,
        details: dossier.anef_dernier_titre
          ? [`Un titre provisoire vous a été délivré : ${dossier.anef_dernier_titre}`, ...detailsFromStatus]
          : detailsFromStatus,
        canSpeak: true,
      });
    }

    // 4. Titre fabriqué ou délivré (final)
    if (dossier.anef_dernier_titre &&
        !dossier.anef_dernier_titre.toLowerCase().includes('prolongation') &&
        !dossier.anef_dernier_titre.toLowerCase().includes('attestation')) {
      // Distinguer entre fabrication et délivrance
      const isFabrication = statutLower.includes('titre_fabrique');
      const label = isFabrication
        ? 'Titre de séjour fabriqué'
        : 'Titre de séjour délivré';
      const details = isFabrication
        ? [
            `${dossier.anef_dernier_titre}`,
            '📋 Votre titre de séjour a été fabriqué avec succès !',
            '📞 Vous serez très prochainement convoqué pour venir le retirer.',
            '⚠️ Important : Vérifiez que votre numéro de téléphone est bien à jour dans vos paramètres pour recevoir la convocation.'
          ]
        : [dossier.anef_dernier_titre];

      timeline.push({
        type: isFabrication ? 'TITRE_FABRIQUE' : 'TITRE',
        label: label,
        date: dossier.anef_dernier_titre_date || dossier.anef_derniere_mise_a_jour,
        completed: !isFabrication, // Fabriqué = pas encore complètement terminé
        current: isFabrication, // Si fabriqué, c'est l'étape actuelle
        details: details,
      });
    }

    return timeline.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <LinearProgress sx={{ width: '100%', maxWidth: 300, mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Chargement des détails...
        </Typography>
      </Box>
    );
  }

  if (!dossier) {
    return (
      <Box sx={{ minHeight: '100vh', p: 2 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Error color="error" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h6" gutterBottom>Dossier non trouvé</Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/client/dashboard')}
            sx={{ mt: 2 }}
            fullWidth
          >
            Retour au dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  const timeline = buildTimeline();
  const currentStep = timeline.findIndex(step => step.current);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 10 }}>
      {/* Header fixe */}
      <Paper
        elevation={2}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          borderRadius: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, gap: 1 }}>
          <IconButton onClick={() => navigate('/client/dashboard')} size="small">
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '0.95rem', lineHeight: 1.2 }}>
              {dossier.titre || dossier.type_demande || 'Dossier ANEF'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              N° {dossier.anef_numero_demande || dossier.numero_demande || 'N/A'}
            </Typography>
          </Box>
          <Chip
            size="small"
            icon={dossier.anef_has_complement ? <Warning /> : <HourglassEmpty />}
            label={dossier.anef_statut_actuel?.replace(/_/g, ' ').substring(0, 15) || 'En cours'}
            color={dossier.anef_has_complement ? 'warning' : 'info'}
            sx={{ fontWeight: 'bold', fontSize: '0.7rem', maxWidth: 140 }}
          />
          <IconButton onClick={handleSync} disabled={syncing} size="small">
            <Sync className={syncing ? 'rotating' : ''} fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      {/* Contenu principal */}
      <Container maxWidth="md" sx={{ mt: 3, px: 2 }}>
        {/* Timeline verticale */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontSize: '1.1rem', fontWeight: 'bold' }}>
            📍 Suivi en temps réel
          </Typography>

          <Stepper activeStep={currentStep} orientation="vertical">
            {timeline.map((step, index) => (
              <Step key={index} completed={step.completed}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: step.completed ? 'success.main' : step.current ? 'warning.main' : 'grey.300',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      {getStepIcon(step.type)}
                    </Box>
                  )}
                >
                  <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                    {step.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(step.date).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    {Array.isArray(step.details) ? (
                      step.details.map((detail: string, idx: number) => (
                        <Paper
                          key={idx}
                          elevation={0}
                          sx={{
                            p: 2,
                            mb: 1,
                            bgcolor: step.type === 'COMPLEMENT' ? 'warning.50' : 'grey.50',
                            borderLeft: step.type === 'COMPLEMENT' ? '4px solid' : 'none',
                            borderColor: 'warning.main',
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {detail}
                          </Typography>
                          {step.canSpeak && voiceEnabled && (
                            <Button
                              size="small"
                              startIcon={speaking ? <VolumeOff /> : <VolumeUp />}
                              onClick={() => handleSpeakText(detail)}
                              variant={speaking ? 'contained' : 'outlined'}
                              sx={{ mt: 2 }}
                              fullWidth
                            >
                              {speaking ? 'Arrêter la lecture' : 'Écouter'}
                            </Button>
                          )}
                        </Paper>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {step.details}
                      </Typography>
                    )}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Notifications ANEF */}
        {dossier.anef_notifications && dossier.anef_notifications.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem', fontWeight: 'bold' }}>
              🔔 Notifications ANEF
            </Typography>
            {dossier.anef_notifications.map((notification: any, index: number) => {
              const motifNotif = notification.motif_notification || 'INFO';
              const estLu = notification.lu || !!notification.date_lecture;
              const estRepondu = !!notification.date_reponse;
              const isImportant = motifNotif === 'DEMANDE_COMPLEMENT' && !estRepondu;

              return (
                <Accordion
                  key={notification.id || index}
                  defaultExpanded={index === 0 && isImportant}
                  sx={{
                    mb: 1,
                    borderLeft: '4px solid',
                    borderColor:
                      motifNotif === 'DEMANDE_COMPLEMENT' ? 'warning.main' :
                      motifNotif === 'DECISION' ? 'success.main' :
                      motifNotif === 'PROLONGATION_INSTRUCTION' ? 'info.main' :
                      'grey.400',
                    '&:before': { display: 'none' },
                    opacity: estLu ? 0.85 : 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      bgcolor:
                        motifNotif === 'DEMANDE_COMPLEMENT' ? 'warning.50' :
                        motifNotif === 'DECISION' ? 'success.50' :
                        motifNotif === 'PROLONGATION_INSTRUCTION' ? 'info.50' :
                        'grey.50',
                      minHeight: 48,
                      '&.Mui-expanded': { minHeight: 48 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor:
                            motifNotif === 'DEMANDE_COMPLEMENT' ? 'warning.main' :
                            motifNotif === 'DECISION' ? 'success.main' :
                            motifNotif === 'PROLONGATION_INSTRUCTION' ? 'info.main' :
                            'grey.400',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {motifNotif === 'DEMANDE_COMPLEMENT' ? (
                          <Warning sx={{ color: 'white', fontSize: 20 }} />
                        ) : motifNotif === 'DECISION' ? (
                          <CheckCircle sx={{ color: 'white', fontSize: 20 }} />
                        ) : (
                          <Notifications sx={{ color: 'white', fontSize: 20 }} />
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                            {motifNotif === 'DEMANDE_COMPLEMENT' ? 'Demande de complément' :
                             motifNotif === 'PROLONGATION_INSTRUCTION' ? 'Prolongation instruction' :
                             motifNotif === 'DECISION' ? 'Décision' :
                             motifNotif.replace(/_/g, ' ')}
                          </Typography>
                          <Chip
                            size="small"
                            label={estLu ? "👁️ Lu" : "🔴 Non lu"}
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: estLu ? 'grey.200' : 'error.light',
                              color: estLu ? 'text.secondary' : 'white',
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          📅 {notification._created
                            ? new Date(notification._created).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : 'Date inconnue'}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2, bgcolor: 'white' }}>
                    {/* Message selon le type de notification */}
                    {motifNotif === 'DEMANDE_COMPLEMENT' ? (
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', mb: 2 }}>
                          L'administration vous a demandé des documents complémentaires.
                        </Typography>

                        {/* Chips de statut */}
                        {(() => {
                          const dateCreation = new Date(notification._created);
                          const dateReponse = notification.date_reponse ? new Date(notification.date_reponse) : null;
                          const dateLimite = new Date(dateCreation);
                          dateLimite.setDate(dateLimite.getDate() + 30);
                          const joursRestants = Math.ceil((dateLimite.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                          const estRepondu = !!dateReponse;
                          const estEnRetard = joursRestants < 0 && !estRepondu;

                          return (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {estRepondu ? (
                                <Chip
                                  icon={<CheckCircle />}
                                  label={`Répondu le ${dateReponse.toLocaleDateString('fr-FR')}`}
                                  color="success"
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              ) : estEnRetard ? (
                                <Chip
                                  icon={<Error />}
                                  label={`URGENT - Délai dépassé (${dateLimite.toLocaleDateString('fr-FR')})`}
                                  color="error"
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              ) : (
                                <Chip
                                  icon={joursRestants <= 7 ? <Warning /> : <Schedule />}
                                  label={`${joursRestants} jour${joursRestants > 1 ? 's' : ''} restant${joursRestants > 1 ? 's' : ''} (${dateLimite.toLocaleDateString('fr-FR')})`}
                                  color={joursRestants <= 7 ? 'warning' : 'info'}
                                  size="small"
                                  sx={{ fontWeight: 'bold' }}
                                />
                              )}
                            </Box>
                          );
                        })()}

                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, fontStyle: 'italic' }}>
                          💡 Consultez l'historique des documents demandés ci-dessous pour voir les détails
                        </Typography>
                      </Box>
                    ) : motifNotif === 'PROLONGATION_INSTRUCTION' ? (
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                        Votre dossier est en cours d'instruction. Une attestation de prolongation vous a été délivrée.
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary', fontStyle: 'italic' }}>
                        Notification reçue de l'ANEF
                      </Typography>
                    )}

                    {/* Statut de lecture */}
                    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {estLu && notification.date_lecture && (
                          <>👁️ Lu le {new Date(notification.date_lecture).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}</>
                        )}
                        {!estLu && '🔴 Non lu sur ANEF - Veuillez consulter votre compte'}
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Paper>
        )}

        {/* Historique des demandes de complément */}
        {dossier.anef_demandes_complement && dossier.anef_demandes_complement.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem', fontWeight: 'bold' }}>
              📎 Historique des documents demandés
            </Typography>
            {dossier.anef_demandes_complement.map((complement: any, index: number) => {
              const estRepondu = !!complement.date_reponse;
              const estLu = complement.lu || !!complement.date_lecture;
              const estValide = complement.justificatifs?.every((j: any) =>
                j.justificatif?.statut === 'VALIDE' || j.justificatif?.statut === 'CONFIRME'
              );
              const estEnAttente = complement.justificatifs?.some((j: any) =>
                j.justificatif?.statut === 'EN_ATTENTE'
              );

              return (
                <Accordion
                  key={complement.id || index}
                  defaultExpanded={index === 0 && !estRepondu}
                  sx={{
                    mb: 1,
                    borderLeft: '4px solid',
                    borderColor: estValide ? 'success.main' : estRepondu ? 'info.main' : 'warning.main',
                    '&:before': { display: 'none' },
                    opacity: estRepondu ? 0.9 : 1,
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore />}
                    sx={{
                      bgcolor: estValide ? 'success.50' : estRepondu ? 'info.50' : 'warning.50',
                      minHeight: 48,
                      '&.Mui-expanded': { minHeight: 48 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', pr: 1 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          bgcolor: estValide ? 'success.main' : estRepondu ? 'info.main' : 'warning.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {estValide ? (
                          <CheckCircle sx={{ color: 'white', fontSize: 20 }} />
                        ) : estRepondu ? (
                          <HourglassEmpty sx={{ color: 'white', fontSize: 20 }} />
                        ) : (
                          <Warning sx={{ color: 'white', fontSize: 20 }} />
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                          📅 {new Date(complement._created).toLocaleDateString('fr-FR')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          <Chip
                            size="small"
                            label={estLu ? "👁️ Lu" : "🔴 Non lu"}
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: estLu ? 'grey.200' : 'error.light',
                              color: estLu ? 'text.secondary' : 'white',
                            }}
                          />
                          <Chip
                            size="small"
                            label={estRepondu ? "✅ Répondu" : "⏰ En attente de réponse"}
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: estRepondu ? 'success.light' : 'warning.light',
                              color: 'white',
                            }}
                          />
                          <Chip
                            size="small"
                            label={
                              estValide ? "✅ Validés" :
                              estEnAttente ? "⏳ En cours de traitement" :
                              estRepondu ? "📨 Envoyés" :
                              "⚠️ À envoyer"
                            }
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2, bgcolor: 'white' }}>
                    {complement.justificatifs && complement.justificatifs.length > 0 && (
                      <Box>
                        {complement.justificatifs.map((justif: any, jIndex: number) => {
                          const statutDoc = justif.justificatif?.statut;
                          const observation = justif.justificatif?.historique_echanges?.[0]?.observation;
                          const motif = justif.justificatif?.historique_echanges?.[0]?.motif;

                          return (
                            <Box
                              key={jIndex}
                              sx={{
                                p: 1.5,
                                mb: 1,
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.85rem' }}>
                                    {justif.justificatif?.type?.replace(/_/g, ' ')}
                                  </Typography>
                                  {motif && (
                                    <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.5 }}>
                                      <strong>Motif:</strong> {motif.replace(/_/g, ' ')}
                                    </Typography>
                                  )}
                                </Box>
                                {statutDoc && (
                                  <Chip
                                    size="small"
                                    label={
                                      statutDoc === 'VALIDE' || statutDoc === 'CONFIRME' ? '✅ Validé' :
                                      statutDoc === 'EN_ATTENTE' ? '⏳ En attente' :
                                      statutDoc === 'REFUSE' ? '❌ Refusé' :
                                      statutDoc
                                    }
                                    color={
                                      statutDoc === 'VALIDE' || statutDoc === 'CONFIRME' ? 'success' :
                                      statutDoc === 'EN_ATTENTE' ? 'info' :
                                      statutDoc === 'REFUSE' ? 'error' :
                                      'default'
                                    }
                                  />
                                )}
                              </Box>

                              {observation && (
                                <Box sx={{ mt: 1, p: 1, bgcolor: 'white', borderRadius: 1 }}>
                                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                    <strong>Ce qui est demandé:</strong>
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                                    {observation}
                                  </Typography>
                                  {voiceEnabled && (
                                    <Button
                                      size="small"
                                      startIcon={speaking ? <VolumeOff /> : <VolumeUp />}
                                      onClick={() => handleSpeakText(observation)}
                                      variant="outlined"
                                      sx={{ mt: 1, fontSize: '0.75rem' }}
                                      fullWidth
                                    >
                                      {speaking ? 'Arrêter' : 'Écouter'}
                                    </Button>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                    {/* Informations de suivi */}
                    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {estLu && complement.date_lecture && (
                          <Typography variant="caption" color="text.secondary">
                            👁️ Lu le {new Date(complement.date_lecture).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        )}
                        {!estLu && (
                          <Typography variant="caption" color="error.main" sx={{ fontWeight: 'bold' }}>
                            🔴 Non lu sur ANEF - Veuillez consulter votre compte ANEF
                          </Typography>
                        )}
                        {estRepondu && (
                          <Typography variant="caption" color="success.main" sx={{ fontWeight: 'bold' }}>
                            ✅ Répondu le {new Date(complement.date_reponse).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        )}
                        {!estRepondu && (
                          <Typography variant="caption" color="warning.main" sx={{ fontWeight: 'bold' }}>
                            ⏰ En attente de votre réponse sur ANEF
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Paper>
        )}

        {/* Historique des titres (si disponible) */}
        {dossier.anef_historique_titres && dossier.anef_historique_titres.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem', fontWeight: 'bold' }}>
              📜 Historique des titres
            </Typography>
            {dossier.anef_historique_titres.map((titre: any, index: number) => (
              <Card key={index} sx={{ mb: 2, bgcolor: 'grey.50' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                    <Description color="primary" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                        {titre.nature || 'Titre de séjour'}
                      </Typography>
                      {titre.motif_delivrance && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {titre.motif_delivrance}
                        </Typography>
                      )}
                      {titre.date_debut_validite && titre.date_fin_validite && (
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            size="small"
                            label={`Du ${new Date(titre.date_debut_validite).toLocaleDateString('fr-FR')}`}
                          />
                          <Chip
                            size="small"
                            label={`Au ${new Date(titre.date_fin_validite).toLocaleDateString('fr-FR')}`}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Paper>
        )}

        {/* Informations complémentaires */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem', fontWeight: 'bold' }}>
            ℹ️ Informations
          </Typography>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Type de demande</Typography>
              <Typography variant="body1">
                {dossier.anef_data_raw?.type_demande_sejour || dossier.anef_data_raw?.formulaire || dossier.type_demande || 'N/A'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Date de dépôt</Typography>
              <Typography variant="body1">
                {(dossier.date_depot || dossier.anef_date_depot)
                  ? new Date(dossier.date_depot || dossier.anef_date_depot!).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })
                  : 'N/A'}
              </Typography>
            </Box>
            {dossier.anef_prefecture && (
              <Box>
                <Typography variant="caption" color="text.secondary">Préfecture</Typography>
                <Typography variant="body1">{dossier.anef_prefecture}</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Container>

      {/* Bouton d'action flottant */}
      {dossier.anef_has_complement && voiceEnabled && (
        <Fab
          color="warning"
          variant="extended"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            left: 16,
            mx: 'auto',
            maxWidth: 400,
          }}
          onClick={() => {
            const complementStep = timeline.find(s => s.type === 'COMPLEMENT');
            if (complementStep?.details?.[0]) {
              handleSpeakText(complementStep.details.join('\n\n'));
            }
          }}
        >
          <VolumeUp sx={{ mr: 1 }} />
          Écouter les documents demandés
        </Fab>
      )}

      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
      `}</style>
    </Box>
  );
}
